import React from 'react';
import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui';

const BuyerPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-orange-500 rounded-full mr-3" aria-hidden="true"></div>
            <span className="text-lg font-semibold">Vesta • Legal Center</span>
          </div>
          <BackButton
            onClick={() => window.history.back()}
            variant="link"
            size="small"
            color="#C97B53"
            ariaLabel="Back to Legal Center"
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Policy Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Buyer Policy
          </h1>
          <p className="text-gray-600 mb-4">
            Effective Date: [Insert Date]
          </p>
          <p className="text-gray-700">
            By placing an order, booking a service, or using the Vesta Platform ("Vesta," "we," "us"), you agree to this Buyer Policy, our Terms of Service, Prohibited Items Policy, and other applicable policies. This policy outlines your responsibilities as a buyer and what you can expect from sellers.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              1. Buying on Vesta
            </h2>
            <p className="text-gray-700 mb-4">
              When you make a purchase or booking on Vesta, you are entering into a direct contract with the independent seller ("Seller") who created the listing. Vesta facilitates the transaction but does not produce, package, store, or ship food or beverages.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Your Responsibilities as a Buyer
            </h2>
            <p className="text-gray-700 mb-4">You agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Read Listings Carefully</strong> – Review all descriptions, lead times, pickup/delivery options, pricing, and applicable service terms before purchasing.</li>
              <li><strong>Review Allergen Information</strong> – Check full ingredient and allergen disclosures; contact the Seller before ordering if you have dietary concerns.</li>
              <li><strong>Provide Accurate Information</strong> – Give correct contact details, delivery addresses, and any special instructions.</li>
              <li><strong>Be Available for Delivery/Pickup</strong> – Be ready to receive orders during the agreed time window or collect them promptly.</li>
              <li><strong>Prepare for In-Home Services</strong> – For catering or chef bookings, ensure safe, clean, and adequately equipped premises.</li>
              <li><strong>Handle Food Safely</strong> – Refrigerate, freeze, or consume items promptly according to safe handling guidelines.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Delivery & Pickup Expectations
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Delivery</strong> – Risk transfers upon confirmed handoff (photo, signature, or code verification).</li>
              <li><strong>Pickup</strong> – Orders should be collected within the agreed timeframe; uncollected orders may be discarded without refund.</li>
              <li><strong>Missed Deliveries/Pickups</strong> – May result in cancellation without refund at the Seller's discretion if rescheduling is not possible.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Preorders & Custom Orders
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Review lead times before purchasing.</li>
              <li>Some Sellers require non-refundable deposits.</li>
              <li>Cancellations may only be allowed before preparation begins; check Seller's terms.</li>
              <li>Provide any required design or customization details promptly to avoid delays.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. In-Home Chef & Catering Bookings
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Chefs and caterers on Vesta are independent contractors.</li>
              <li>Ensure the work area is sanitary, has necessary utilities (power, water, cooking equipment), and is safe to work in.</li>
              <li>Disclose relevant hazards (e.g., pets, allergies in the household).</li>
              <li>Vesta is not liable for property damage or injuries during service.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Communication & Conduct
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Keep all order-related communication on Vesta.</li>
              <li>Respond to Seller messages promptly (within 24–48 hours).</li>
              <li>Maintain respectful and professional communication at all times.</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Refunds, Returns & Disputes
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Due to the perishable nature of food, refunds are generally limited to safety or quality issues.</li>
              <li>Taste preference alone is not a valid reason for a refund.</li>
              <li>If there's an issue, contact the Seller first; if unresolved within 48 hours, you may escalate to Vesta for review.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Reviews & Feedback
            </h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>You may leave a review within the review window after an order is marked complete.</li>
              <li>Reviews should be truthful and based on your own experience.</li>
              <li>Review manipulation (false reviews, incentivized reviews) is prohibited.</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Prohibited Buyer Activities
            </h2>
            <p className="text-gray-700 mb-4">You may not:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Place fraudulent or speculative orders.</li>
              <li>Request off-platform transactions for items listed on Vesta.</li>
              <li>Harass Sellers or misuse the dispute process.</li>
              <li>Misrepresent your identity or circumstances.</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Consequences for Violations
            </h2>
            <p className="text-gray-700 mb-4">Violation of this policy may result in:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Warning or account suspension.</li>
              <li>Removal of the ability to purchase or book services.</li>
              <li>Permanent account closure.</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Policy Updates
            </h2>
            <p className="text-gray-700">
              We may update this Buyer Policy to reflect changes in law, industry practices, or Platform operations. Updates will be communicated via email or in-app notifications. Continued use of Vesta after changes constitutes acceptance.
            </p>
          </section>

          {/* Section 12 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For questions about this Buyer Policy or to report violations, please contact us at:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Email: legal@vesta.com</li>
              <li>Support: support@vesta.com</li>
              <li>Phone: [Insert Phone Number]</li>
            </ul>
          </section>

          {/* Section 13 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Governing Law
            </h2>
            <p className="text-gray-700">
              This Buyer Policy is governed by the laws of [Insert Jurisdiction] and any disputes will be resolved in accordance with our Terms of Service.
            </p>
          </section>

          {/* Section 14 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Severability
            </h2>
            <p className="text-gray-700">
              If any provision of this Buyer Policy is found to be unenforceable or invalid, the remaining provisions will continue to be in full force and effect.
            </p>
          </section>

          {/* Section 15 */}
          <section className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Entire Agreement
            </h2>
            <p className="text-gray-700">
              This Buyer Policy, together with our Terms of Service and other applicable policies, constitutes the entire agreement between you and Vesta regarding your use of the platform as a buyer.
            </p>
          </section>

          {/* Final Statement */}
          <section className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <p className="text-orange-800 text-center font-medium">
              By using Vesta, you acknowledge that you have read, understood, and agree to be bound by this Buyer Policy.
            </p>
          </section>
        </div>

        {/* Footer Navigation */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/legal"
            className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
            aria-label="Return to Legal Center"
          >
            Return to Legal Center
          </Link>
        </div>
      </main>
    </div>
  );
};

export default BuyerPolicy; 